/*
 * @flow
 */
import LunarDate from '../LunarDate'
import {
  STEMS,
  BRANCHES,
  PALACES,
  WU_XINGS,
  MAIN_STARS,
  EMPEROR_STARS_OFFSET,
  TREASURY_STARS_OFFSET,
  YEAR_STEM_STARS,
  YEAR_STEM_STARS_RULE,
  STEM_YEAR_APPLYS,
  STARS_BRIGHTNESS,
  APPLY_STARS,
  MONTHLY_STARS,
  MONTHLY_STARS_OFFSET,
  HOURLY_STARS,
  DAILY_STARS,
  YEAR_BRANCH_STARS,
  OTHER_STARS,
  YEAR12_STARS,
  GENERAL12_STARS,
  GENERAL12_STARS_OFFSET,
  DOCTOR12_STARS,
  LONGLIFE12_STARS,
  LONGLIFE12_STARS_OFFSET,
  SELF_LORD,
  LIFE_LORD,
  YINGYANG,
  ASTROLABE_VERSION,
} from './constants'
import Star from './star'

export { ASTROLABE_VERSION }

/**
 * Astrolabe class
 *
 * with properties,
 *
 * @param lunarDate Date in Lunar Calendar
 * @param sex Sex identifier of user
 */
export default class Astrolabe {
  constructor(
    lunarDate: LunarDate,
    sex: 1 | 0,
    version: number = ASTROLABE_VERSION.FULL,
  ) {
    let isLeap2Next = false
    let computedMonth = lunarDate.month
    this.selfIndex = 0
    this.lifeIndex = 0

    this.branches = Array(12)
    for (let i = 0; i < 12; i++) {
      this.branches[i] = {
        name: BRANCHES[i],
        mainStars: [],
        auxStars: [],
        minorStars: [],
        g12Stars: [],
        ll12Stars: [],
        ageYears: []
      }
    }

    // try {
    let { selfIndex, lifeIndex } = resolvePalaces(this.branches)
    this.selfIndex = selfIndex
    this.lifeIndex = lifeIndex
    let wuxingIndex = resolveWuxing(this.branches, this.selfIndex)
    this.wuxing = WU_XINGS[wuxingIndex].name
    locateTreasuryStars(locateEmperorStars(wuxingIndex, this.branches))
    if (version === ASTROLABE_VERSION.FULL) {
      locateYearStemStars(this.branches)
      locateMonthStars(this.branches)
      // console.log('wuxing: ', this.wuxing)
      locateHourStars(this.branches)
      locateYearBranchStars(this.branches, this.selfIndex, this.lifeIndex)
      locateOtherStars(this.branches, this.selfIndex)
      this.ziweiSex = locateG12StarsDecades(this.branches)
    }
    if (version >= ASTROLABE_VERSION.SIMPLE) {
      locateAgeDestiny(this.branches)
      const { selfLord, lifeLord } = getLords(this.selfIndex)
      this.selfLord = selfLord
      this.lifeLord = lifeLord
      const { zidou, currentZidou } = getZidous()
      this.zidou = zidou
      this.currentZidou = currentZidou
    }
    // } catch(err) {
    //   console.error(err)
    // }

    /* private functions */
    function resolvePalaces(branches) {
      let selfIndex, lifeIndex = 0
      if (lunarDate.isLeap && lunarDate.day >= 15) {
        isLeap2Next = true
        console.debug("lunar month before change to leap: " + computedMonth)
        computedMonth++
        console.debug("lunar month after change to leap: " + computedMonth)
      }
      let startHour = computedMonth + 2 // From '寅'
      selfIndex = (startHour-lunarDate.lunarHour+11) % 12
      lifeIndex = (startHour+lunarDate.lunarHour-1) % 12
      branches[selfIndex].palaceIndex = 0
      branches[selfIndex].palaceName = PALACES[0]
      branches[lifeIndex].isLifePalace = true

      // Placing all branches
      for (var i = 1; i < 12; i++) {
        let idx = (i + selfIndex) % 12
        branches[idx].palaceIndex = i
        branches[idx].palaceName = PALACES[i]
      }
      return { selfIndex, lifeIndex }
    }

    function resolveWuxing(branches, selfIndex) {
      let stemArray = new Array(12)
      let startStem = 10 - lunarDate.yearStem
      startStem === 1 && (startStem = 11)
      stemArray[startStem] = lunarDate.yearStem
      for (let i = 1; i < 12; i++) {
        let idx = (startStem + i) % 12
        let prevIdx = (idx + 11) % 12
        stemArray[idx] = idx === 2 ? stemArray[0] : stemArray[prevIdx] + 1
      }
      let selfStem = stemArray[selfIndex]
      let startWuxing = Math.floor(selfStem / 2)
      console.debug('startWuxing: ' + startWuxing)

      // Assgining stem to palaces
      stemArray.forEach(function(elem, idx) {
        var stemIdx = elem % 10
        branches[idx].stemIndex = stemIdx
        branches[idx].stemName = STEMS[stemIdx]
      })

      return (startWuxing + Math.floor(selfIndex / 2) % 3) % 5
    }

    function resolveBornYearApplys(star) {
      let bornApplys = STEM_YEAR_APPLYS[lunarDate.yearStem]
      star.apply = bornApplys.indexOf(star.type + star.index)
      star.applyName = APPLY_STARS[star.apply]
      return star
    }

    function resolveBrightness(star, palaceIdx) {
      star.brightness = STARS_BRIGHTNESS[star.type + star.index][palaceIdx]
      return star
    }

    function locateEmperorStars(wuxingIndex, branches) {
      let emperorIdx = -1

      let birthdate = lunarDate.hour === 23 ? Math.round(lunarDate.day) : Math.floor(lunarDate.day)
      let wuxingValue = WU_XINGS[wuxingIndex].value
      let x = (wuxingValue - birthdate % wuxingValue) % wuxingValue
      let quotient = (birthdate + x) / wuxingValue
      if (x === 0) {
        emperorIdx = (quotient + 1) % 12
      } else if (x % 2 === 0) {
        emperorIdx = (quotient + 1 + x) % 12
      } else {
        emperorIdx = (quotient + 13 - x) % 12
      }

      // Locating stars of emperor
      let currentIdx = emperorIdx
      for (let i = 0; i < EMPEROR_STARS_OFFSET.length; i++) {
        currentIdx = (currentIdx + 12 - EMPEROR_STARS_OFFSET[i]) % 12
        let star = new Star('MAIN', i, MAIN_STARS[i])
        branches[currentIdx].mainStars.push(resolveBornYearApplys(resolveBrightness(star, currentIdx)))
      }

      return { emperorIdx, branches }
    }

    function locateTreasuryStars({ emperorIdx, branches }) {
      let treasuryIdx = (16 - emperorIdx) % 12

      // Locating remaining stars of treasury
      let currentIdx = treasuryIdx
      for (let i = 0; i < TREASURY_STARS_OFFSET.length; i++) {
        currentIdx = (currentIdx + TREASURY_STARS_OFFSET[i]) % 12
        let star = new Star('MAIN', i + 6, MAIN_STARS[i + 6])
        branches[currentIdx].mainStars.push(resolveBornYearApplys(resolveBrightness(star, currentIdx)))
      }
    }

    // 安年干星系：存魁鉞羊陀, 天官, 天褔, 天廚, 截空
    function locateYearStemStars(branches) {
      let phecdaIdx = YEAR_STEM_STARS_RULE[lunarDate.yearStem][0]
      branches[phecdaIdx].auxStars.push(new Star('AUX', 0, YEAR_STEM_STARS[0]))
      let palaceIdx = ( phecdaIdx + 1) % 12
      branches[palaceIdx].auxStars.push(resolveBrightness(new Star('AUX', 8, YEAR_STEM_STARS[3]), palaceIdx))
      palaceIdx = ( phecdaIdx + 11 ) % 12
      branches[palaceIdx].auxStars.push(resolveBrightness(new Star('AUX', 9, YEAR_STEM_STARS[4]), palaceIdx))

      // 安魁鉞
      branches[YEAR_STEM_STARS_RULE[lunarDate.yearStem][1]].auxStars.push(new Star('AUX', 1, YEAR_STEM_STARS[1]))
      branches[YEAR_STEM_STARS_RULE[lunarDate.yearStem][2]].auxStars.push(new Star('AUX', 2, YEAR_STEM_STARS[2]))

      branches[YEAR_STEM_STARS_RULE[lunarDate.yearStem][3]].minorStars.push(new Star('MINOR', 2, YEAR_STEM_STARS[5]))
      branches[YEAR_STEM_STARS_RULE[lunarDate.yearStem][4]].minorStars.push(new Star('MINOR', 3, YEAR_STEM_STARS[6]))
      branches[YEAR_STEM_STARS_RULE[lunarDate.yearStem][5]].minorStars.push(new Star('MINOR', 4, YEAR_STEM_STARS[7]))
      branches[YEAR_STEM_STARS_RULE[lunarDate.yearStem][6]].minorStars.push(new Star('MINOR', 5, YEAR_STEM_STARS[8]))      
    }

    // 安月系星：左輔, 右弼, 天馬, 天刑, 天姚, (月)解神, 陰煞, 天巫, 天月
    function locateMonthStars(branches) {
      // 安輔弼天馬
      let leftIndex = (MONTHLY_STARS_OFFSET[0] + lunarDate.month - 1) % 12
      branches[leftIndex].auxStars.push(resolveBornYearApplys(new Star('AUX', 5, MONTHLY_STARS[0])))
      let rightIndex = (MONTHLY_STARS_OFFSET[1] - lunarDate.month + 13) % 12
      branches[rightIndex].auxStars.push(resolveBornYearApplys(new Star('AUX', 6, MONTHLY_STARS[1])))
      branches[(MONTHLY_STARS_OFFSET[2] - (lunarDate.month - 1) * 3 + 36) % 12].auxStars.push(new Star('AUX', 7, MONTHLY_STARS[2]))
      // 安天刑, 天姚, (月)解神, 陰煞, 天巫, 天月
      branches[(MONTHLY_STARS_OFFSET[3] + lunarDate.month - 1) % 12].minorStars.push(new Star('MINOR', 6, MONTHLY_STARS[3]))
      branches[(MONTHLY_STARS_OFFSET[4] + lunarDate.month - 1) % 12].minorStars.push(new Star('MINOR', 7, MONTHLY_STARS[4]))
      branches[(MONTHLY_STARS_OFFSET[5] + 2 * Math.floor((lunarDate.month - 1) / 2)) % 12].minorStars.push(new Star('MINOR', 8, MONTHLY_STARS[5]))
      branches[(MONTHLY_STARS_OFFSET[6] - lunarDate.month * 2 + 26) % 12].minorStars.push(new Star('MINOR', 9, MONTHLY_STARS[6]))
      branches[MONTHLY_STARS_OFFSET[7][(lunarDate.month - 1) % 4]].minorStars.push(new Star('MINOR', 10, MONTHLY_STARS[7]))
      branches[MONTHLY_STARS_OFFSET[8][lunarDate.month - 1]].minorStars.push(new Star('MINOR', 11, MONTHLY_STARS[8]))

      // 安日系星：三台, 八座
      branches[(leftIndex + lunarDate.day - 1) % 12].minorStars.push(new Star('MINOR', 14, DAILY_STARS[0]))
      branches[(rightIndex - lunarDate.day % 12 + 12 + 1) % 12].minorStars.push(new Star('MINOR', 15, DAILY_STARS[1]))
    }

    // 安時系星：文昌, 文曲, 火星, 鈴星, 地劫, 地空, 台輔, 封誥
    function locateHourStars(branches) {
      // 安昌曲
      let litPalaceIdx = (HOURLY_STARS[0].offset - lunarDate.lunarHour + 12) % 12
      branches[litPalaceIdx].auxStars.push(resolveBornYearApplys(resolveBrightness(new Star('AUX', 3, HOURLY_STARS[0].name), litPalaceIdx)))
      let elqPalaceIdx = (HOURLY_STARS[1].offset + lunarDate.lunarHour) % 12
      branches[elqPalaceIdx].auxStars.push(resolveBornYearApplys(resolveBrightness(new Star('AUX', 4, HOURLY_STARS[1].name), elqPalaceIdx)))
      // 安火鈴
      let turmoilIdx = HOURLY_STARS[2].offset[lunarDate.yearBranch % 4]
      let turbulenceIdx = HOURLY_STARS[3].offset[lunarDate.yearBranch % 4]
      let palaceIdx = (turmoilIdx + lunarDate.lunarHour) % 12
      branches[palaceIdx].auxStars.push(resolveBrightness(new Star('AUX', 10, HOURLY_STARS[2].name), palaceIdx))
      palaceIdx = (turbulenceIdx + lunarDate.lunarHour) % 12
      branches[palaceIdx].auxStars.push(resolveBrightness(new Star('AUX', 11, HOURLY_STARS[3].name), palaceIdx))
      // 安劫空
      branches[(HOURLY_STARS[4].offset + lunarDate.lunarHour) % 12].auxStars.push(new Star('AUX', 12, HOURLY_STARS[4].name))
      branches[(HOURLY_STARS[5].offset - lunarDate.lunarHour + 12) % 12].auxStars.push(new Star('AUX', 13, HOURLY_STARS[5].name))
      // 安台輔, 封誥
      branches[(elqPalaceIdx + 2) % 12].minorStars.push(new Star('MINOR', 12, HOURLY_STARS[6].name))
      branches[(elqPalaceIdx - 2 + 12) % 12].minorStars.push(new Star('MINOR', 13, HOURLY_STARS[7].name))

      // 安日系星：恩光, 天貴
      branches[(litPalaceIdx + lunarDate.day - 2) % 12].minorStars.push(new Star('MINOR', 16, DAILY_STARS[2]))
      branches[(elqPalaceIdx + lunarDate.day - 2) % 12].minorStars.push(new Star('MINOR', 17, DAILY_STARS[3]))
    }

    // 安年支星系：紅鸞, 天喜, 天空, 天哭, 天虚, 龍池, 鳳閣, 孤辰, 寡宿, 蜚廉, 破碎, 月德，大耗, 天才, 天壽
    function locateYearBranchStars(branches, selfIndex, lifeIndex) {
      // 安紅鸞天喜
      let appealIdx = (YEAR_BRANCH_STARS[0].offset - lunarDate.yearBranch + 12) % 12
      branches[appealIdx].minorStars.push(new Star('MINOR', 0, YEAR_BRANCH_STARS[0].name))
      branches[(appealIdx + 6) % 12].minorStars.push(new Star('MINOR', 1, YEAR_BRANCH_STARS[1].name))
      // 安天空, 天哭, 天虛, 龍池, 鳳閣
      branches[(lunarDate.yearBranch + 1) % 12].minorStars.push(new Star('MINOR', 18, YEAR_BRANCH_STARS[2].name))
      branches[(YEAR_BRANCH_STARS[3].offset - lunarDate.yearBranch + 12) % 12].minorStars.push(new Star('MINOR', 19, YEAR_BRANCH_STARS[3].name))
      branches[(YEAR_BRANCH_STARS[4].offset + lunarDate.yearBranch) % 12].minorStars.push(new Star('MINOR', 20, YEAR_BRANCH_STARS[4].name))
      branches[(YEAR_BRANCH_STARS[5].offset + lunarDate.yearBranch) % 12].minorStars.push(new Star('MINOR', 21, YEAR_BRANCH_STARS[5].name))
      branches[(YEAR_BRANCH_STARS[6].offset - lunarDate.yearBranch + 12) % 12].minorStars.push(new Star('MINOR', 22, YEAR_BRANCH_STARS[6].name))
      // 安孤辰, 寡宿, 蜚廉, 破碎, 月德, 天才, 天壽
      let isoIdx = Math.floor((lunarDate.yearBranch + 1) % 12 / 3)
      branches[YEAR_BRANCH_STARS[7].offset[isoIdx]].minorStars.push(new Star('MINOR', 23, YEAR_BRANCH_STARS[7].name))
      branches[YEAR_BRANCH_STARS[8].offset[isoIdx]].minorStars.push(new Star('MINOR', 24, YEAR_BRANCH_STARS[8].name))
      branches[YEAR_BRANCH_STARS[9].offset[lunarDate.yearBranch]].minorStars.push(new Star('MINOR', 25, YEAR_BRANCH_STARS[9].name))
      branches[YEAR_BRANCH_STARS[10].offset[lunarDate.yearBranch % 3]].minorStars.push(new Star('MINOR', 26, YEAR_BRANCH_STARS[10].name))
      // branches[YEAR_BRANCH_STARS[11].offset[lunarDate.yearBranch % 4]].minorStars.push(new Star('MINOR', 27, YEAR_BRANCH_STARS[11].name))
      // branches[YEAR_BRANCH_STARS[12].offset[lunarDate.yearBranch % 4]].minorStars.push(new Star('MINOR', 28, YEAR_BRANCH_STARS[12].name))
      // branches[(YEAR_BRANCH_STARS[13].offset + lunarDate.yearBranch) % 12].minorStars.push(new Star('MINOR', 29, YEAR_BRANCH_STARS[13].name))
      branches[(YEAR_BRANCH_STARS[11].offset + lunarDate.yearBranch) % 12].minorStars.push(new Star('MINOR', 27, YEAR_BRANCH_STARS[11].name))
      // branches[YEAR_BRANCH_STARS[15].offset[lunarDate.yearBranch % 4]].minorStars.push(new Star('MINOR', 31, YEAR_BRANCH_STARS[15].name))
      // branches[(YEAR_BRANCH_STARS[16].offset + lunarDate.yearBranch) % 12].minorStars.push(new Star('MINOR', 32, YEAR_BRANCH_STARS[16].name))
      // branches[(YEAR_BRANCH_STARS[17].offset + lunarDate.yearBranch) % 12].minorStars.push(new Star('MINOR', 33, YEAR_BRANCH_STARS[17].name))
      branches[(selfIndex + lunarDate.yearBranch) % 12].minorStars.push(new Star('MINOR', 28, YEAR_BRANCH_STARS[12].name))
      branches[(lifeIndex + lunarDate.yearBranch) % 12].minorStars.push(new Star('MINOR', 29, YEAR_BRANCH_STARS[13].name))
    }

    // 安干支星系：旬空, 及其他：天傷, 天使
    function locateOtherStars(branches, selfIndex) {
      // 安旬空
      // branches[(lunarDate.yearBranch + )]

      // 安天傷, 天使
      branches[(selfIndex + 5) % 12].minorStars.push(new Star('MINOR', 37, OTHER_STARS[1]))
      branches[(selfIndex + 7) % 12].minorStars.push(new Star('MINOR', 38, OTHER_STARS[2]))
    }

    // 安四大神煞 & 大限
    function locateG12StarsDecades(branches) {
      // 安太歲十二神
      for (let i = 0; i < 12; i++) {
        branches[(lunarDate.yearBranch + i) % 12].g12Stars.push(new Star('YEAR12', i, YEAR12_STARS[i]))
      }
      // 安將前十二神
      let startIdx = GENERAL12_STARS_OFFSET[lunarDate.yearBranch % 4]
      for (let i = 0; i < 12; i++) {
        branches[(startIdx + i) % 12].g12Stars.push(new Star('GENERAL12', i, GENERAL12_STARS[i]))
      }

      // 定陰陽順逆
      let isYang = lunarDate.yearStem % 2 === 0 ? 1 : 0
      let inc = 1
      if (isYang !== sex) {
        inc = -1
      }
      // 安博士十二神
      startIdx = YEAR_STEM_STARS_RULE[lunarDate.yearStem][0]
      for (let i = 0; i < 12; i++) {
        let j = i * inc
        branches[(startIdx + j + 12) % 12].g12Stars.push(new Star('DOCTOR12', i, DOCTOR12_STARS[i]))
      }
      // 安長生十二神
      let startYear = WU_XINGS[wuxingIndex].value
      console.debug('startYear wuxing: ', startYear)
      let longlifeStart = LONGLIFE12_STARS_OFFSET[wuxingIndex];
      for (let i = 0; i < 12; i++) {
        let j = i * inc
        branches[(selfIndex + j + 12) % 12].decade = [startYear + i * 10, startYear + i * 10 + 9]
        branches[(longlifeStart + j + 12) % 12].ll12Stars.push(new Star('LONGLIFE12', i, LONGLIFE12_STARS[i]))
      }

      console.debug('isYang: ', isYang, ', sex: ', sex, 'YINGYANG: ', isYang << 1 | sex)
      return YINGYANG[isYang << 1 | sex]
    }

    // 定小限
    function locateAgeDestiny(branches) {
      let oneAgeIndex = ((4 - 3 * ((lunarDate.yearBranch + 10) % 4)) + 12) % 12
      let multiplier = sex ? 1 : -1
      for (let i = 0; i < 100; i++) {
        branches[(oneAgeIndex + ( i % 12) * multiplier + 12) % 12].ageYears.push(i + 1)
      }
    }

    // 安命主身主
    function getLords(selfIndex) {
      // Get self-lord
      let selfLordIndex = selfIndex
      if (selfLordIndex > 6) {
        selfLordIndex = 12 - selfLordIndex
      }
      return { selfLord: SELF_LORD[selfLordIndex], lifeLord: LIFE_LORD[lunarDate.yearBranch % 6] }
    }

    // 定子斗流斗
    function getZidous() {
      let monthBranch = (13 - lunarDate.month) % 12
      let zidouIndex = (monthBranch + lunarDate.lunarHour) % 12
      let thisYear = new LunarDate(new Date())
      monthBranch = (thisYear.yearBranch - lunarDate.month + 13) % 12
      let currentZidouIndex = (monthBranch + lunarDate.lunarHour) % 12

      return { zidou: BRANCHES[zidouIndex], currentZidou: BRANCHES[currentZidouIndex] }
    }

  }

}